import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { initialsOf } from '@domain/entities/Person';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { formatMonth } from '../format';
import { PAGE_SIZES, type PagerControls } from '../pagination';
import styles from './primitives.module.css';

/**
 * The design system.
 *
 * Every component here is presentational: it takes what to render and what to call,
 * and it knows nothing about Firebase, use cases or routing. That is what "vistas
 * tontas" means in practice — these can be rendered in a test or a preview with
 * plain objects, and the same button behaves identically wherever it appears.
 *
 * Styling comes entirely from CSS Modules over the tokens in `tokens.css`. Where a
 * value is genuinely data-driven — a sub area's colour, a progress ratio — it is
 * passed as a CSS custom property rather than an inline style, so the stylesheet
 * stays the single description of how something looks.
 */

const cx = (...classes: readonly (string | false | null | undefined)[]): string =>
  classes.filter((c): c is string => typeof c === 'string' && c.length > 0).join(' ');

/** A CSS custom property, typed so the compiler accepts the `--*` key. */
const cssVars = (vars: Readonly<Record<string, string | number>>): CSSProperties =>
  vars;

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface CardProps {
  readonly children: ReactNode;
  readonly title?: string;
  readonly note?: string;
  readonly action?: ReactNode;
  /** Draws the design's 4px accent rail on the top edge. */
  readonly accentColor?: string;
  /*
   * `| undefined` explícito: con `exactOptionalPropertyTypes` no basta el `?`, y
   * las clases de un módulo CSS llegan tipadas como `string | undefined`.
   */
  readonly className?: string | undefined;
  readonly as?: 'div' | 'section' | 'article';
}

export const Card = ({
  children,
  title,
  note,
  action,
  accentColor,
  className,
  as: Element = 'section',
}: CardProps): ReactNode => (
  <Element
    className={cx(styles.card, accentColor !== undefined && styles.cardAccent, className)}
    style={accentColor !== undefined ? cssVars({ '--accent': accentColor }) : undefined}
  >
    {(title !== undefined || action !== undefined) && (
      <header className={styles.cardHeader}>
        <div>
          {title !== undefined && <h2 className={styles.cardTitle}>{title}</h2>}
          {note !== undefined && <p className={styles.cardNote}>{note}</p>}
        </div>
        {action}
      </header>
    )}
    {children}
  </Element>
);

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'success' | 'danger';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  readonly small?: boolean;
  readonly block?: boolean;
  /** Shows a spinner and disables the button. */
  readonly busy?: boolean;
}

export const Button = ({
  variant = 'primary',
  small = false,
  block = false,
  busy = false,
  disabled = false,
  children,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode => (
  <button
    type={type}
    className={cx(
      styles.button,
      styles[variant],
      small && styles.small,
      block && styles.block,
    )}
    disabled={disabled || busy}
    // Communicates the pending state to assistive technology, which a spinner alone
    // does not.
    aria-busy={busy || undefined}
    {...rest}
  >
    {busy && <Spinner />}
    {children}
  </button>
);

export const Spinner = ({ label }: { label?: string }): ReactNode => (
  <>
    <span className={styles.spinner} aria-hidden="true" />
    {label !== undefined && <span className="visually-hidden">{label}</span>}
  </>
);

// ---------------------------------------------------------------------------
// Chip
// ---------------------------------------------------------------------------

export interface ChipProps {
  readonly children: ReactNode;
  readonly background?: string;
  readonly foreground?: string;
  readonly count?: number;
}

/** A non-interactive status pill. */
export const Chip = ({ children, background, foreground, count }: ChipProps): ReactNode => (
  <span
    className={styles.chip}
    style={cssVars({
      ...(background !== undefined ? { '--chip-bg': background } : {}),
      ...(foreground !== undefined ? { '--chip-fg': foreground } : {}),
    })}
  >
    {children}
    {count !== undefined && <span className={styles.chipCount}>{count}</span>}
  </span>
);

export interface FilterChipProps {
  readonly children: ReactNode;
  readonly active: boolean;
  readonly count?: number;
  readonly onClick: () => void;
  readonly color?: string;
}

/**
 * A chip that filters. A real `<button>` with `aria-pressed`, not a styled div —
 * the design's filter rows are the primary navigation of several screens and have to
 * be keyboard-reachable and announced as toggles.
 */
export const FilterChip = ({
  children,
  active,
  count,
  onClick,
  color,
}: FilterChipProps): ReactNode => (
  <button
    type="button"
    className={cx(styles.chip, styles.chipButton, active && styles.chipActive)}
    style={
      active && color !== undefined
        ? cssVars({ '--chip-bg': color, '--chip-fg': '#fff' })
        : undefined
    }
    aria-pressed={active}
    onClick={onClick}
  >
    {children}
    {count !== undefined && <span className={styles.chipCount}>{count}</span>}
  </button>
);

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

export interface AvatarProps {
  readonly name: string;
  /** Sub-area accent colour. The design has no photographs; initials over colour. */
  readonly color: string;
  readonly size?: number;
  readonly photoURL?: string | null;
  /**
   * Abre la ficha de esta persona. `null` —el reposo— lo deja como adorno.
   *
   * Se recibe una función y no un id porque el sistema de diseño no sabe de rutas ni
   * de permisos. Quien decide las dos cosas es `usePersonLink`, que devuelve `null`
   * a quien no tiene `ver_detalle`: así el avatar no se vuelve pulsable para
   * prometer algo que la pantalla siguiente va a negar.
   */
  readonly onOpen?: (() => void) | null;
}

const AvatarMark = ({ name, color, size = 38, photoURL = null }: AvatarProps): ReactNode => {
  const style = cssVars({
    '--avatar-color': color,
    width: `${String(size)}px`,
    height: `${String(size)}px`,
    // The handoff derives both the radius and the type size from the avatar size, so
    // one number drives the whole shape.
    borderRadius: `${String(Math.round(size / 2.8))}px`,
    fontSize: `${String(Math.round(size / 2.7))}px`,
  });

  if (photoURL !== null && photoURL.length > 0) {
    return (
      <img
        src={photoURL}
        alt=""
        className={styles.avatar}
        style={style}
        // The name is already adjacent in every usage, so the image is decorative and
        // announcing it again would be noise.
        aria-hidden="true"
      />
    );
  }

  return (
    <span className={styles.avatar} style={style} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
};

/**
 * El avatar, adorno por omisión y botón cuando lleva a algún sitio.
 *
 * Dos detalles que costaron su vuelta y no se ven en el resultado:
 *
 * - **Deja de ser `aria-hidden`.** Estático era decorativo con razón: el nombre va
 *   escrito al lado en todos sus usos, y anunciarlo otra vez sería ruido. Pulsable
 *   no puede serlo — un control que un lector de pantalla no anuncia es un control
 *   que no existe— así que el botón lleva su propio nombre accesible y la marca de
 *   dentro se queda oculta, que es lo que evita oír el nombre dos veces.
 * - **Detiene su propio `dragstart`.** En los tableros de Aprobaciones la tarjeta
 *   entera es un asa de arrastre, así que sin esto empujar el avatar levantaría la
 *   tarjeta en vez de abrir la ficha. Es la misma trampa que ya se pagó con el
 *   chevron de las tarjetas plegadas.
 */
export const Avatar = (props: AvatarProps): ReactNode => {
  const { name, size = 38, onOpen = null } = props;

  if (onOpen === null) return <AvatarMark {...props} />;

  return (
    <button
      type="button"
      className={styles.avatarButton}
      style={cssVars({ borderRadius: `${String(Math.round(size / 2.8))}px` })}
      onClick={onOpen}
      draggable={false}
      onDragStart={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      aria-label={`Ver la ficha de ${name}`}
    >
      <AvatarMark {...props} />
    </button>
  );
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export interface TabOption<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly count?: number;
}

export interface TabsProps<T extends string> {
  readonly options: readonly TabOption<T>[];
  readonly active: T;
  readonly onChange: (id: T) => void;
  readonly ariaLabel: string;
}

export const Tabs = <T extends string>({
  options,
  active,
  onChange,
  ariaLabel,
}: TabsProps<T>): ReactNode => (
  <div className={cx(styles.tabs, 'scroll-x')} role="tablist" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={option.id === active}
        className={cx(styles.tab, option.id === active && styles.tabActive)}
        onClick={() => {
          onChange(option.id);
        }}
      >
        {option.label}
        {option.count !== undefined && <span className={styles.chipCount}> {option.count}</span>}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface PaginationProps {
  /** Lo que devuelve `usePagination`. */
  readonly pager: PagerControls;
  /** Qué se pagina, en plural y en minúsculas: «personas», «movimientos». */
  readonly noun: string;
  /**
   * Para una lista que no vive dentro de una tarjeta.
   *
   * El pie trae una raya encima, que dentro de un panel se lee como el cierre
   * de la lista. Debajo de una fila de tarjetas sueltas —las solicitudes de
   * «Mis ausencias», el tablero del calendario— esa raya queda flotando sobre el
   * fondo de la página, así que ahí va sin ella.
   */
  readonly detached?: boolean;
}

/**
 * El pie de una lista paginada: cuántos se ven, cuántos por página, y los dos
 * botones.
 *
 * **No se pinta si no hace falta.** Una lista de siete no necesita un «1–7 de
 * 7» con dos botones apagados debajo; `needed` lo decide con el total, ver
 * `needsPaging`.
 *
 * Los botones se apagan en los extremos en vez de desaparecer: si «Anterior» se
 * fuera en la primera página, «Siguiente» saltaría de sitio justo cuando alguien
 * lo está pulsando una y otra vez.
 */
export const Pagination = ({ pager, noun, detached = false }: PaginationProps): ReactNode => {
  const sizeId = useId();
  if (!pager.needed) return null;

  return (
    <nav
      className={cx(styles.pager, detached && styles.pagerDetached)}
      aria-label={`Páginas de ${noun}`}
    >
      {/* `polite`: quien usa lector de pantalla se entera de que la lista cambió
          al pulsar, que si no es un botón que no hace nada. */}
      <span className={styles.pagerRange} aria-live="polite">
        {/* Una página con un solo renglón dice «11», no «11–11». */}
        {pager.from === pager.to ? pager.from : `${String(pager.from)}–${String(pager.to)}`} de{' '}
        {pager.open === true ? 'más de ' : ''}
        {pager.total} {noun}
      </span>

      <label className={styles.pagerSize} htmlFor={sizeId}>
        Por página
        <select
          id={sizeId}
          className={styles.pagerSelect}
          value={pager.pageSize}
          onChange={(event) => {
            pager.setPageSize(Number(event.target.value));
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.pagerButtons}>
        <button
          type="button"
          className={styles.pagerButton}
          disabled={pager.page <= 1}
          onClick={() => {
            pager.goTo(pager.page - 1);
          }}
        >
          ‹ Anterior
        </button>
        <span className={styles.pagerPage}>
          Página {pager.page}
          {pager.open === true ? '' : ` de ${String(pager.pageCount)}`}
        </span>
        <button
          type="button"
          className={styles.pagerButton}
          disabled={pager.page >= pager.pageCount}
          onClick={() => {
            pager.goTo(pager.page + 1);
          }}
        >
          Siguiente ›
        </button>
      </div>
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export interface ProgressBarProps {
  /** 0-1. Clamped, so a breach of a cap cannot overflow the track. */
  readonly ratio: number;
  readonly color?: string;
  readonly label: string;
}

export const ProgressBar = ({ ratio, color, label }: ProgressBarProps): ReactNode => {
  const clamped = Math.max(0, Math.min(1, ratio));
  return (
    <div
      className={styles.progressTrack}
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={styles.progressFill}
        style={cssVars({
          width: `${String(clamped * 100)}%`,
          ...(color !== undefined ? { '--progress-color': color } : {}),
        })}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// KPI grid
// ---------------------------------------------------------------------------

export interface KpiItem {
  readonly label: string;
  readonly value: string | number;
  readonly note?: string;
  readonly color?: string;
}

/** The handoff's grouped KPI card: a hairline grid with a colour dot per cell. */
export const KpiGrid = ({ items }: { items: readonly KpiItem[] }): ReactNode => (
  <div className={styles.kpiGrid}>
    {items.map((item) => (
      <div key={item.label} className={styles.kpiCell}>
        <span className={styles.kpiLabel}>
          <span
            className={styles.kpiDot}
            style={item.color !== undefined ? cssVars({ '--dot-color': item.color }) : undefined}
          />
          {item.label}
        </span>
        <span className={styles.kpiValue}>{item.value}</span>
        {item.note !== undefined && <span className={styles.kpiNote}>{item.note}</span>}
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

export type BannerTone = 'info' | 'warn' | 'danger' | 'success';

const BANNER_CLASS: Readonly<Record<BannerTone, string>> = {
  info: styles.bannerInfo ?? '',
  warn: styles.bannerWarn ?? '',
  danger: styles.bannerDanger ?? '',
  success: styles.bannerSuccess ?? '',
};

const BANNER_GLYPH: Readonly<Record<BannerTone, string>> = {
  info: 'i',
  warn: '!',
  danger: '!',
  success: '✓',
};

export interface BannerProps {
  readonly tone: BannerTone;
  readonly children: ReactNode;
  readonly action?: ReactNode;
}

export const Banner = ({ tone, children, action }: BannerProps): ReactNode => (
  <div
    className={cx(styles.banner, BANNER_CLASS[tone])}
    // `alert` interrupts a screen reader; reserved for the tones that genuinely
    // need immediate attention.
    role={tone === 'danger' || tone === 'warn' ? 'alert' : 'status'}
  >
    <span className={styles.bannerGlyph} aria-hidden="true">
      {BANNER_GLYPH[tone]}
    </span>
    <span style={{ flex: 1 }}>{children}</span>
    {action}
  </div>
);

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  readonly glyph?: string;
  readonly title: string;
  readonly note?: string;
  readonly action?: ReactNode;
}

export const EmptyState = ({ glyph = '·', title, note, action }: EmptyStateProps): ReactNode => (
  <div className={styles.empty}>
    <span className={styles.emptyGlyph} aria-hidden="true">
      {glyph}
    </span>
    <p className={styles.emptyTitle}>{title}</p>
    {note !== undefined && <p className={styles.emptyNote}>{note}</p>}
    {action}
  </div>
);

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export interface ModalProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly closeLabel?: string;
  /**
   * Un modal que no se puede cerrar sin contestar.
   *
   * Quita la ✕ y desactiva Escape. Existe para uno solo —la confirmación de datos
   * al entrar— y por eso viene apagado: encerrar a alguien en un diálogo es lo que
   * hace odiosa una pantalla, y sólo se justifica cuando las salidas están dentro,
   * que ahí son «Todo está correcto» y «Algo está mal».
   */
  readonly dismissible?: boolean;
  /**
   * Cuánto espacio pide el contenido.
   *
   * `wide` existe para los modales que crecen según lo que el usuario despliega —
   * la baja abre con un formulario corto y gana el desglose del finiquito cuando
   * hay sueldo. Con el ancho fijo, ese desglose se apila en una columna estrecha y
   * el modal se vuelve un scroll largo; a 960 caben las dos cosas lado a lado.
   *
   * No es una talla libre a propósito: tres valores con nombre se pueden razonar,
   * un número arbitrario por sitio de llamada acaba en cinco anchos distintos.
   *
   * `full` es el tercero y el más raro: lo pide el alta de una persona, que tiene
   * trece campos obligatorios y una columna de documentos al lado. A 960 el
   * formulario medía 1081px de alto —un rollo de siete filas— y ensancharlo sin
   * darle sitio a los campos no arreglaba nada: la columna de la izquierda le
   * quitaba a la derecha lo que el panel ganaba. A 1180 caben tres campos por
   * fila y el alto baja a la mitad.
   */
  readonly size?: 'default' | 'wide' | 'full';
  /**
   * Un marco delgado, para cuando el contenido **es** la pantalla.
   *
   * Lo pide el visor de documentos: ahí cada píxel de alto que gasta el marco es
   * un píxel menos de hoja. La franja del título medía 76px —44 del botón de
   * cerrar, que es mínimo táctil y no se recorta, más 16 arriba y abajo— y el
   * cuerpo ponía otros 24 de relleno por lado; entre las dos cosas y la barra de
   * acciones, el documento se quedaba con poco más de la mitad del diálogo.
   *
   * `viewer` aprieta el marco al mínimo y deja el relleno del cuerpo en lo justo
   * para que la hoja no toque el borde redondeado. No lo usa ningún formulario:
   * en un formulario el aire es lo que hace legible la columna de campos.
   */
  readonly frame?: 'default' | 'viewer';
  /**
   * Lo que va en la franja del título, a la derecha, antes de la ✕.
   *
   * Existe para no gastar una fila entera en los botones: el visor pone ahí el
   * paso entre documentos y las dos acciones del archivo, y se ahorra los 44px
   * de una barra propia.
   */
  readonly headerActions?: ReactNode;
}

/**
 * A modal dialog.
 *
 * Three behaviours the design implies but does not draw, all of which matter for
 * a form this long: Escape closes it, focus moves inside on open, and the page
 * behind it does not scroll. Without the last one, a mobile user dragging inside the
 * wizard scrolls the page underneath instead.
 */
export const Modal = ({
  title,
  onClose,
  children,
  footer,
  closeLabel = 'Cerrar',
  size = 'default',
  dismissible = true,
  frame = 'default',
  headerActions,
}: ModalProps): ReactNode => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  /*
   * Enfocar y bloquear el scroll son cosas **de apertura**, no de cada render.
   *
   * Estaban en el mismo efecto que el listener de Escape, que sí depende de
   * `onClose` — y `onClose` suele ser una función escrita en el render de quien
   * abre el modal, o sea una identidad nueva cada vez. El resultado: cualquier
   * modal que se re-renderice al escribir volvía a llamar `panel.focus()` en cada
   * tecla y **le quitaba el foco al campo**.
   *
   * Se vio al capturar un sueldo en la baja: el primer carácter entraba y el
   * segundo ya no. Parecía un remonte de React —la explicación cómoda— y no lo
   * era: se midió que el nodo del input era el mismo y que `document.activeElement`
   * había pasado a ser el panel. Estaba en el sistema de diseño, así que le pasaba
   * a todos los modales del portal; sólo se nota en los que re-renderizan seguido.
   */
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the panel so the next Tab lands inside the dialog rather than back in
    // the page behind it.
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /* Escape sí necesita el `onClose` vigente, y no toca el foco. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, dismissible]);

  /*
   * Colgado del `body`, no de donde se escribió.
   *
   * El velo es `position: fixed`, que se mide contra la ventana **salvo** que
   * algún ancestro tenga `transform`, `filter`, `backdrop-filter`, `contain` o
   * `will-change`: cualquiera de esas crea un bloque contenedor y el «fijo»
   * pasa a medirse contra esa caja.
   *
   * Le pasó al diálogo del respaldo, que quedó encerrado en su tarjeta con el
   * velo cubriendo sólo esa esquina. La culpable es la animación de entrada de
   * `Card`: lleva `fill-mode: both`, así que el `transform` del último
   * fotograma se queda puesto —identidad, invisible, y suficiente para crear el
   * bloque contenedor— mucho después de que la animación terminara. Nadie
   * relacionaría una animación de aparición con un modal descolocado, que es
   * justo por lo que este arreglo no puede ser «no montes diálogos dentro de
   * una tarjeta»: la próxima trampa será otra propiedad en otro ancestro.
   *
   * Un portal quita la pregunta de en medio: da igual qué CSS caiga mañana
   * sobre un ancestro, el diálogo no cuelga de él.
   */
  return createPortal(
    <div
      className={styles.overlay}
      onClick={(event) => {
        // Only a click on the backdrop itself closes; a click that started inside
        // the panel and ended on the overlay must not. Y si el modal es
        // obligatorio, tampoco el velo: sería la salida que la ✕ no da.
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={cx(
          styles.modal,
          size === 'wide' && styles.modalWide,
          size === 'full' && styles.modalFull,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className={cx(styles.modalHeader, frame === 'viewer' && styles.modalHeaderSlim)}>
          <h2
            id={titleId}
            className={cx(styles.modalTitle, frame === 'viewer' && styles.modalTitleSlim)}
          >
            {title}
          </h2>
          {headerActions === undefined ? (
            dismissible && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label={closeLabel}
              >
                ×
              </button>
            )
          ) : (
            <div className={styles.modalHeaderActions}>
              {headerActions}
              {dismissible && (
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={onClose}
                  aria-label={closeLabel}
                >
                  ×
                </button>
              )}
            </div>
          )}
        </header>
        <div className={cx(styles.modalBody, frame === 'viewer' && styles.modalBodyFlush)}>
          {children}
        </div>
        {footer !== undefined && <footer className={styles.modalFooter}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

export interface FieldProps {
  readonly label: string;
  readonly children: (id: string) => ReactNode;
  readonly error?: string | null;
  readonly hint?: string;
}

/**
 * Wraps a control with a properly associated label.
 *
 * The child is a function of the generated id so the `<label for>` link cannot be
 * forgotten — a placeholder-only field is invisible to a screen reader, and this
 * form is long enough that it would matter.
 */
export const Field = ({ label, children, error = null, hint }: FieldProps): ReactNode => {
  const id = useId();
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {hint !== undefined && <span className={styles.kpiNote}>{hint}</span>}
      {error !== null && (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export const inputClass = (invalid = false): string =>
  cx(styles.input, invalid && styles.inputInvalid);

export const selectClass = (): string => styles.select ?? '';
export const textareaClass = (): string => styles.textarea ?? '';
export const sectionLabelClass = (): string => styles.sectionLabel ?? '';

export { styles as dsStyles, cx, cssVars };

// ---------------------------------------------------------------------------
// Month switcher
// ---------------------------------------------------------------------------

export interface MonthSwitcherProps {
  /** Cualquier día del mes a la vista. */
  readonly month: CalendarDate;
  readonly today: CalendarDate;
  readonly onChange: (next: CalendarDate) => void;
  /** Rótulo del grupo para lectores de pantalla. */
  readonly label: string;
}

/**
 * `‹ Agosto 2026 › Hoy` — el cambiador de mes, compartido.
 *
 * Vive aquí y no en una pantalla porque son dos las que lo usan —el calendario del
 * área y el tablero de aprobaciones— y son de las que se miran una detrás de otra.
 * Dos copias se separan: primero en el hueco, luego en si «Hoy» va antes o después
 * de las flechas, y acaban leyéndose como dos controles distintos que hacen lo
 * mismo.
 *
 * Las flechas van pegadas al nombre del mes, que es lo que cambian: separadas, hay
 * que mirar a otro sitio para saber a qué mes se llegó. «Hoy» va detrás de las dos
 * y no entre ellas — no es un paso más de la misma secuencia, es la salida de ella
 * — y se apaga cuando ya se está en el mes actual: así además señala dónde estás.
 */
export const MonthSwitcher = ({
  month,
  today,
  onChange,
  label,
}: MonthSwitcherProps): ReactNode => {
  const showingThisMonth = month.year === today.year && month.month === today.month;

  return (
    <div className={styles.monthSwitcher} role="group" aria-label={label}>
      <button
        type="button"
        className={styles.monthNavButton}
        onClick={() => {
          onChange(month.addMonths(-1));
        }}
        aria-label="Mes anterior"
      >
        ‹
      </button>
      {/* `aria-live`: para quien no ve el repintado, pulsar la flecha no dice nada
          si el nombre del mes cambia en silencio. */}
      <strong className={styles.monthLabel} aria-live="polite">
        {formatMonth(month)}
      </strong>
      <button
        type="button"
        className={styles.monthNavButton}
        onClick={() => {
          onChange(month.addMonths(1));
        }}
        aria-label="Mes siguiente"
      >
        ›
      </button>
      <button
        type="button"
        className={cx(styles.monthNavButton, styles.monthTodayButton)}
        disabled={showingThisMonth}
        onClick={() => {
          onChange(today);
        }}
        aria-label={showingThisMonth ? 'Ya estás en el mes actual' : 'Ir al mes actual'}
      >
        Hoy
      </button>
    </div>
  );
};
